/* eslint-disable max-lines-per-function */
/* eslint-disable id-length */
import React, { useState, useMemo, useRef, useLayoutEffect } from 'react';
import {
  Box,
  Select,
  MenuItem,
  InputLabel,
  ListSubheader,
  TextField,
  InputAdornment,
  IconButton,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { ReactComponent as SearchGlass } from '../../Assets/images/svg/dar_search.svg';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PropTypes from 'prop-types';
import './dropdown.scss';
import { SEARCH } from '../../Constants/strings';
import ClearIcon from '@mui/icons-material/Clear';

const containsText = (text, searchText) =>
  text?.toLowerCase().indexOf(searchText.toLowerCase()) > -1;

const getOptionValue = (option) => {
  if (option && typeof option === 'object') {
    return option.id ?? option.value ?? option.name ?? option.label;
  }
  return option;
};

const SearchDropdown = ({
  label,
  options = [],
  onChange,
  placeholder = 'Select',
  value,
  dropdownCss,
  searchBox = true,
  hideSearchOnSingle = false,
  disabled,
  clearIcon = false,
  customCss = {},
  menuZIndex,
  disablePortal = false,
  emptyMenuText = '',
  multiSelect = false,
  initialVisibleCount,
}) => {
  const [searchText, setSearchText] = useState('');
  const rootRef = useRef(null);
  const searchInputRef = useRef(null);
  const [triggerWidth, setTriggerWidth] = useState(0);

  const measureTriggerWidth = () => {
    if (rootRef.current) {
      setTriggerWidth(rootRef.current.offsetWidth);
    }
  };

  useLayoutEffect(() => {
    measureTriggerWidth();
  }, []);

  const showSearchInput
    = searchBox && (!hideSearchOnSingle || options.length > 1);

  const selectedName = useMemo(() => {
    if (multiSelect) {
      return Array.isArray(value) ? value.map(getOptionValue) : [];
    }
    if (!value) return '';
    if (typeof value === 'string') return value;
    return value?.name || value?.label || '';
  }, [multiSelect, value]);

  const placeholderText = String(placeholder || 'Select').trim() || 'Select';

  const displayedOptions = useMemo(() => {
    const normalizedOptions = Array.isArray(options) ? options : [];
    const filteredOptions = normalizedOptions.filter((option) => {
      const optionName = typeof option === 'string' ? option : (option.name || option.label || '');
      return containsText(optionName, searchText);
    });

    if (!searchText.trim() && Number.isInteger(initialVisibleCount) && initialVisibleCount > 0) {
      return filteredOptions.slice(0, initialVisibleCount);
    }

    return filteredOptions;
  }, [searchText, options, initialVisibleCount]);

  const handleClear = (event) => {
    event.stopPropagation();
    onChange({ target: { value: multiSelect ? [] : '' } });
  };

  const hasSelection = multiSelect
    ? Array.isArray(value) && value.length > 0
    : Boolean(value);

  const renderClearButton = () => clearIcon && hasSelection ? (
    <IconButton
      className="dropdown-clear-icon"
      aria-label="Clear selection"
      title="Clear selection"
      size="small"
      disabled={disabled}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={handleClear}
    >
      <ClearIcon />
    </IconButton>
  ) : null;

  return (
    <Box
      ref={rootRef}
      className={`common-dropdown-root ${dropdownCss}`}
    >
      {label ? <InputLabel className="dropdown-input-label">{label}</InputLabel> : null}
      <Select
        className="common-dropdown"
        MenuProps={{
          disablePortal,
          ...(menuZIndex ? { sx: { zIndex: menuZIndex } } : {}),
          PaperProps: {
            className: multiSelect ? 'dropdown-paper dropdown-paper-multiselect' : 'dropdown-paper',
            style: triggerWidth
              ? { width: triggerWidth, minWidth: 'unset' }
              : { minWidth: 'unset' },
          },
          TransitionProps: {
            onEntered: () => {
              if (searchInputRef.current) {
                searchInputRef.current.focus();
              }
            },
          },
          autoFocus: false,
        }}
        id="search-dropdown"
        IconComponent={KeyboardArrowDownIcon}
        multiple={multiSelect}
        value={selectedName}
        onChange={(event) => {
          if (multiSelect) {
            const selectedValues = event.target.value;
            const selectedOptions = selectedValues.map((selectedValue) =>
              options.find((option) => getOptionValue(option) === selectedValue)
                ?? selectedValue,
            );
            onChange({ target: { value: selectedOptions } });
            return;
          }

          const selectedOption = options.find((opt) => {
            const optName = typeof opt === 'string' ? opt : (opt.name || opt.label);
            return optName === event.target.value;
          });
          onChange({ target: { value: selectedOption } });
        }}
        onClose={() => setSearchText('')}
        onOpen={measureTriggerWidth}
        displayEmpty
        renderValue={(selected) => {
          if (multiSelect) {
            if (!Array.isArray(selected) || selected.length === 0) {
              return <span className="dropdown-render-value dropdown-render-placeholder">{placeholderText}</span>;
            }

            const selectedLabels = selected.map((selectedValue) => {
              const option = options.find((item) => getOptionValue(item) === selectedValue);
              return option
                ? (typeof option === 'string' ? option : (option.name || option.label || ''))
                : String(selectedValue);
            });

            return (
              <span
                className="dropdown-render-value"
                title={selectedLabels.join(', ')}
              >
                <span className="dropdown-render-text">{selectedLabels.join(', ')}</span>
                {renderClearButton()}
              </span>
            );
          }

          const displayText = String(selected || '').trim();
          return (
            <span
              className={`dropdown-render-value${displayText ? '' : ' dropdown-render-placeholder'}`}
              title={displayText || placeholderText}
            >
              <span className="dropdown-render-text">{displayText || placeholderText}</span>
              {renderClearButton()}
            </span>
          );
        }}
        sx={customCss}
        fullWidth
        size="small"
        disabled={disabled}
      >
        {showSearchInput && (
          <ListSubheader onClick={(e) => e.stopPropagation()}>
            <TextField
              className="dropdown-text-field"
              size="small"
              autoFocus
              inputRef={searchInputRef}
              placeholder={SEARCH}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchGlass />
                  </InputAdornment>
                ),
              }}
              onChange={(event) => setSearchText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== 'Escape') {
                  event.stopPropagation();
                }
              }}
            />
          </ListSubheader>
        )}
        {emptyMenuText ? (
          <MenuItem className="dropdown-menu-item" value="">
            <span>{emptyMenuText}</span>
          </MenuItem>
        ) : null}
        {displayedOptions.map((option, index) => {
          const optionName = typeof option === 'string' ? option : (option.name || option.label || '');
          const optionValue = getOptionValue(option) ?? index;
          const optionKey = optionValue;
          const isChecked = multiSelect
            && Array.isArray(selectedName)
            && selectedName.includes(optionValue);

          return (
            <MenuItem
              className="dropdown-menu-item"
              key={optionKey}
              value={multiSelect ? optionValue : optionName}
            >
              {multiSelect ? (
                <>
                  <Checkbox checked={Boolean(isChecked)} />
                  <ListItemText
                    primary={optionName}
                    primaryTypographyProps={{ title: optionName }}
                  />
                </>
              ) : (
                <span title={optionName}>{optionName}</span>
              )}
            </MenuItem>
          );
        })}
      </Select>
    </Box>
  );
};

SearchDropdown.propTypes = {
  options: PropTypes.array.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  value: PropTypes.any,
  dropdownCss: PropTypes.string,
  searchBox: PropTypes.bool,
  hideSearchOnSingle: PropTypes.bool,
  disabled: PropTypes.bool,
  clearIcon: PropTypes.bool,
  customCss: PropTypes.any,
  menuZIndex: PropTypes.number,
  disablePortal: PropTypes.bool,
  emptyMenuText: PropTypes.string,
  multiSelect: PropTypes.bool,
  initialVisibleCount: PropTypes.number,
};

export default SearchDropdown;
